import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { useEffect, useRef, useState, type VFC } from 'react'

import { LOGIN_OTP_STATE } from 'shared/consts'
import {
  ERROR_BADINPUT,
  ERROR_FORBIDDEN,
  ERROR_UNAVAILABLE,
  GENERAL_ERROR,
} from 'shared/consts/errorCodes'
import {
  EMAIL_FIELD,
  LOGIN_REGISTER_ACTION_FIELD,
  OTP_FIELD,
  OTP_INPUT_FORM_ACTION_FIELD_VALUE,
  TARGET_ANCHOR_FIELD,
  TARGET_FEATURE_CODE_FIELD,
  TARGET_PAGE_CODE_FIELD,
  TARGET_PAGE_COMMAND_FIELD,
  TARGET_PRODUCT_CODE_FIELD,
} from 'shared/consts/formFields'
import type { PageProperties } from 'types/pageProperties'
import Form from 'views/components/atoms/Form'
import Input from 'views/components/atoms/Input'
import OneTimePasswordInput from 'views/components/organisms/OneTimePasswordLoginContent/OneTimePasswordInput/OneTimePasswordInput'
import LoadingIndicatorButton from 'views/components/organisms/SellerRating/LoadingIndicatorButton'
import useMountPoints from 'views/enhancers/useMountPoints'
import {
  usePageProperties,
  usePublicConfig,
} from 'views/utils/shopAndRuntimeConfigUtils'

import './OneTimePasswordInputForm.scss'

const WAIT_TIME_FOR_RE_REQUEST_MESSAGE = 30000
const OTP_LENGTH = 6

type ErrorToTranslationType = {
  [key: string]: JSX.Element
  [key: symbol]: JSX.Element
}

const errorToTranslation: ErrorToTranslationType = {
  [GENERAL_ERROR.toString()]: <Trans id="general.error" />,
  [ERROR_BADINPUT.toString()]: <Trans id="login.otp.wrongCode" />,
  [ERROR_FORBIDDEN.toString()]: <Trans id="general.error" />,
  [ERROR_UNAVAILABLE.toString()]: <Trans id="general.error" />,
}

const OneTimePasswordInputForm: VFC = () => {
  const { language } = usePublicConfig()
  const mountPoints = useMountPoints()
  const {
    email,
    isFriendlyCaptchaToggleActive,
    otpLoginErrors = [],
    targetPageParams,
    oneTimePasswordFromUrl,
  } = usePageProperties() as PageProperties & {
    otpLoginErrors: (string | symbol)[]
    oneTimePasswordFromUrl: string | undefined
    isFriendlyCaptchaToggleActive: boolean
  }

  const [loading, setLoading] = useState(false)

  const [clientsideErrorMessage, setClientsideErrorMessage] = useState<
    string | null
  >(null)

  const [showReRequestMessage, setShowReRequestMessage] =
    useState<boolean>(false)

  useEffect(() => {
    // wait for 30s before showing the re-request message
    setTimeout(() => {
      setShowReRequestMessage(true)
    }, WAIT_TIME_FOR_RE_REQUEST_MESSAGE)
  }, [])

  useEffect(() => {
    if (!email) {
      window.location.href = mountPoints.getLogin(
        targetPageParams,
        LOGIN_OTP_STATE.OTP_REQUEST_NEW_EMAIL
      )
    }
  }, [email, mountPoints, targetPageParams])

  const otpInputReference = useRef<HTMLInputElement | null>(null)

  let targetPageCode, targetProductCode, targetAnchor, targetPageCommand

  if (targetPageParams) {
    targetPageCode = targetPageParams.targetPageCode
    targetProductCode = targetPageParams.targetProductCode
    targetAnchor = targetPageParams.targetAnchor
    targetPageCommand = targetPageParams.targetPageCommand
  }

  const translatedErrors =
    otpLoginErrors.length > 0
      ? otpLoginErrors.map(error => errorToTranslation[error])
      : []

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setLoading(true)
    if (
      !otpInputReference ||
      otpInputReference?.current?.value.trim().length === 0
    ) {
      event.preventDefault()
      setClientsideErrorMessage(i18n._('login.otp.noCode'))
      setLoading(false)
    }
  }

  return (
    <>
      <h2 className="o-OneTimePasswordInputForm__subtitle">
        <Trans id="login.otp.withCode" />
      </h2>
      <p className="o-OneTimePasswordInputForm__paragraph">
        <Trans
          components={{ b: <span className="u-font-weight--bold" /> }}
          values={{ email }}
          id="login.otp.emailSent"
        />
      </p>
      <Form
        name="otp-login-form"
        data-clientside-hook="formValidation"
        action={mountPoints.getLogin(
          targetPageParams,
          LOGIN_OTP_STATE.OTP_INPUT
        )}
        method="POST"
        onSubmit={handleSubmit}
      >
        <div className="o-OneTimePasswordInputForm__box a-box u-padding u-background-color--greyLightest">
          <h3>
            <Trans id="login.otp.enterCodeHere" />
          </h3>
          <OneTimePasswordInput
            length={OTP_LENGTH}
            defaultValue={oneTimePasswordFromUrl}
            name={OTP_FIELD}
            hasError={Boolean(
              translatedErrors.length > 0 ||
                (clientsideErrorMessage && clientsideErrorMessage?.length > 0)
            )}
            ref={otpInputReference}
          />
          {!clientsideErrorMessage &&
            translatedErrors.length > 0 &&
            translatedErrors.map((error, index) => (
              <p key={index} className="o-OneTimePasswordInputForm--error">
                {error}
              </p>
            ))}
          {clientsideErrorMessage && (
            <p className="o-OneTimePasswordInputForm--error">
              <Trans id="login.otp.noCode" />
            </p>
          )}
          {showReRequestMessage && (
            <p className="u-margin-large--top u-margin-xx-small--bottom">
              <Trans
                components={{
                  a: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      href={mountPoints.getLogin(
                        targetPageParams,
                        LOGIN_OTP_STATE.OTP_REQUEST_NEW_CODE
                      )}
                      className="o-OneTimePasswordInputForm__link"
                      data-qa-id="otp-re-request-code"
                    />
                  ),
                }}
                id="login.otp.checkSpam"
              />
            </p>
          )}
        </div>
        {targetPageCode && (
          <Input
            type="hidden"
            name={TARGET_PAGE_CODE_FIELD}
            value={targetPageCode}
          />
        )}
        {targetProductCode && (
          <Input
            type="hidden"
            name={TARGET_PRODUCT_CODE_FIELD}
            value={targetProductCode}
          />
        )}
        {targetAnchor && (
          <Input
            type="hidden"
            name={TARGET_ANCHOR_FIELD}
            value={targetAnchor}
          />
        )}
        {targetPageCommand && (
          <Input
            type="hidden"
            name={TARGET_PAGE_COMMAND_FIELD}
            value={targetPageCommand}
          />
        )}
        <Input type="hidden" name={TARGET_FEATURE_CODE_FIELD} value="" />
        <input
          type="hidden"
          name={LOGIN_REGISTER_ACTION_FIELD}
          value={OTP_INPUT_FORM_ACTION_FIELD_VALUE}
        />
        <input type="hidden" name={EMAIL_FIELD} value={email as string} />
        <LoadingIndicatorButton
          id="otp-submit-btn"
          name="otp-input-submit-button"
          loading={loading}
          data-loading-label={i18n._('login.otp.sendCode')}
          disabled={isFriendlyCaptchaToggleActive}
          data-qa-id="otp-input-submit-button"
          className="o-OneTimePasswordInputForm__submit-button"
          type="submit"
        >
          <Trans id="login.otp.sendCode" />
        </LoadingIndicatorButton>
        <div className="u-margin--top u-text-align--center">
          <a
            href={mountPoints.getLogin(
              targetPageParams,
              LOGIN_OTP_STATE.OTP_REQUEST_NEW_EMAIL
            )}
            className="o-OneTimePasswordInputForm__link"
            data-qa-id="otp-change-email"
          >
            <Trans id="login.otp.changeEmail" />
          </a>
        </div>
        <div className="u-margin--top u-margin--bottom u-text-align--center">
          <a
            href={mountPoints.getLogin(
              targetPageParams,
              LOGIN_OTP_STATE.LOGIN_REGISTER
            )}
            className="o-OneTimePasswordInputForm__link"
            data-qa-id="otp-back-to-login-with-password"
          >
            <Trans id="login.otp.backToLoginWithPassword" />
          </a>
        </div>
        {isFriendlyCaptchaToggleActive && (
          <div
            className="frc-captcha"
            id="otpLoginForm-frc-captcha"
            data-sitekey="FCMUL52LQ1I634TN"
            data-lang={language}
          />
        )}
      </Form>
    </>
  )
}

export default OneTimePasswordInputForm
